import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useApplicationContext } from 'ApplicationContext';
import { LicenseInspectionResponse } from 'apiClient/Api';

interface DeviceLicenseDecodeDialogProps {
  open: boolean;
  onClose: () => void;
}

export const DeviceLicenseDecodeDialog: React.FC<DeviceLicenseDecodeDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const context = useApplicationContext();
  const [licenseText, setLicenseText] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LicenseInspectionResponse | null>(null);

  const resetDialog = () => {
    setLicenseText('');
    setFileName('');
    setError(null);
    setResult(null);
  };

  const handleClose = () => {
    resetDialog();
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      setLicenseText(text.trim());
    };
    reader.onerror = () => {
      setError(t('error.fetchFailed'));
    };
    reader.readAsText(file);
  };

  const handleDecode = async (): Promise<void> => {
    if (!licenseText.trim()) {
      setError(t('deviceManagement.licenseDecode.missing'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await context.api.api.licenseValidateLicense({
        licenseString: licenseText.trim(),
      }); 
      setResult(response.data ?? null);
    } catch (err) {
      setError(t('error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{t('deviceManagement.licenseDecode.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Button variant="outlined" component="label">
            {t('deviceManagement.licenseDecode.selectFile')}
            <input type="file" hidden onChange={handleFileChange} />
          </Button>
          {fileName && (
            <Typography variant="body2" color="text.secondary">
              {t('deviceManagement.licenseDecode.fileSelected')}: {fileName}
            </Typography>
          )}
          <TextField
            label={t('deviceManagement.licenseDecode.licenseText')}
            value={licenseText}
            onChange={(event) => setLicenseText(event.target.value)}
            multiline
            minRows={4}
            fullWidth
          />
          {error && (
            <Typography color="error">
              {error}
            </Typography>
          )}
          {result && (
            <Stack spacing={1}>
              <Typography variant="subtitle1">{t('deviceManagement.licenseDecode.resultTitle')}</Typography>
              {result.signatureValid === true && (
                <Alert severity="success">
                  {t('deviceManagement.licenseDecode.signatureValid')}
                </Alert>
              )}
              {result.signatureValid === false && (
                <Alert severity="error">
                  {t('deviceManagement.licenseDecode.signatureInvalid')}
                </Alert>
              )}
              <TextField
                value={
                  result.licenseData
                    ? typeof result.licenseData === 'string'
                      ? JSON.stringify(JSON.parse(result.licenseData), null, 2)
                      : JSON.stringify(result.licenseData, null, 2)
                    : ''
                }
                label={t('deviceManagement.licenseDecode.licenseData')}
                multiline
                minRows={6}
                fullWidth
                slotProps={{ input: { readOnly: true } }}
                placeholder={t('deviceManagement.licenseDecode.noData')}
                sx={
                  result.signatureValid === false
                    ? {
                        '& .MuiOutlinedInput-root fieldset': {
                          borderColor: 'error.main',
                          borderWidth: 2,
                        },
                      }
                    : undefined
                }
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          {t('deviceManagement.licenseDecode.cancel')}
        </Button>
        <Button onClick={handleDecode} variant="contained" color="primary" disabled={loading}>
          {loading ? t('deviceManagement.licenseDecode.decoding') : t('deviceManagement.licenseDecode.decode')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
